/**
 * Helper functions for the grid-based calendar view
 */

export interface EventPosition {
  top: number;
  height: number;
}

export interface TimeSlot {
  hour: number;
  label: string;
}

/**
 * Format time for 12-hour display
 */
export const formatTime12Hour = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${String(minutes).padStart(2, '0')}${ampm}`;
  } catch {
    return '';
  }
};

/**
 * Generate time slots for a 24-hour period (half-hourly)
 */
export const generateTimeSlots = (): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const ampm = h >= 12 ? 'pm' : 'am';
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const minutes = m === 0 ? '' : ':30';
      slots.push({
        hour: h + (m === 30 ? 0.5 : 0),
        label: `${displayHour}${minutes}${ampm}`,
      });
    }
  }
  return slots;
};

/**
 * Parse 12-hour or 24-hour time string and return minutes from midnight
 */
const timeStringToMinutesFromMidnight = (timeStr: string): number => {
  try {
    const str = String(timeStr || '').trim().toUpperCase();
    
    // Check for 12-hour format (e.g., "09:30 PM", "11:00 AM", "9:30PM", etc.)
    // Match: 1-2 digit hour : 2 digit minute, optional seconds, optional whitespace, AM/PM
    const ampmMatch = str.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/);
    if (ampmMatch) {
      let hours = parseInt(ampmMatch[1], 10);
      const minutes = parseInt(ampmMatch[2], 10);
      const period = (ampmMatch[4] || '').toUpperCase();
      
      // Convert to 24-hour format
      if (period === 'PM' && hours !== 12) {
        hours += 12; // 1PM = 13, 11PM = 23
      } else if (period === 'AM' && hours === 12) {
        hours = 0; // 12AM = 00
      }
      // 12PM stays 12, 12AM becomes 0
      
      const result = hours * 60 + minutes;
      return result;
    }
    
    // Check for 24-hour format (e.g., "21:30", "09:30")
    const timeMatch = str.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (timeMatch) {
      const hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      if (hours >= 0 && hours <= 23) {
        return hours * 60 + minutes;
      }
    }
    
    return 0;
  } catch (error) {
    console.error('Error parsing time string:', timeStr, error);
    return 0;
  }
};

/**
 * Parse ISO string and get local hours/minutes (not UTC)
 */
const getLocalTimeFromISO = (isoString: string): { hours: number; minutes: number } => {
  try {
    // Create date from ISO string
    const date = new Date(isoString);
    // Use local time, not UTC time
    return {
      hours: date.getHours(),
      minutes: date.getMinutes(),
    };
  } catch {
    return { hours: 0, minutes: 0 };
  }
};

/**
 * Calculate event position and height based on time
 * Accepts either time strings (12/24-hour format) or ISO date strings
 */
export const getEventPosition = (
  startTime: string,
  endTime: string,
  slotHeight = 40
): EventPosition => {
  try {
    let startMinutes: number;
    let endMinutes: number;
    
    const startStr = String(startTime || '').trim();
    const endStr = String(endTime || '').trim();
    
    // Detect if input is a time string (has colon but not ISO format with T)
    // Time string examples: "09:30 PM", "21:30", "9:30PM"
    // ISO string examples: "2025-12-23T21:30:00.000Z"
    const isStartTimeString = startStr.includes(':') && !startStr.includes('T');
    const isEndTimeString = endStr.includes(':') && !endStr.includes('T');
    
    if (isStartTimeString && isEndTimeString) {
      // Both are time strings - use direct parsing
      startMinutes = timeStringToMinutesFromMidnight(startStr);
      endMinutes = timeStringToMinutesFromMidnight(endStr);
    } else if (isStartTimeString) {
      // Start is time string, end might be ISO
      startMinutes = timeStringToMinutesFromMidnight(startStr);
      endMinutes = timeStringToMinutesFromMidnight(endStr);
    } else {
      // ISO date string format - extract local time
      const startLocal = getLocalTimeFromISO(startStr);
      const endLocal = getLocalTimeFromISO(endStr);
      startMinutes = startLocal.hours * 60 + startLocal.minutes;
      endMinutes = endLocal.hours * 60 + endLocal.minutes;
    }
    
    // Handle case where event spans midnight
    let duration = endMinutes - startMinutes;
    if (duration <= 0) {
      duration = duration + (24 * 60); // Add 24 hours if end is before/at start (midnight wrap)
    }

    // Calendar slots: 48 slots per day (2 per hour = 1 per 30 minutes)
    // Each slot is slotHeight px, so we need (minutes / 30) * slotHeight
    return {
      top: (startMinutes * slotHeight) / 30,
      height: Math.max((duration * slotHeight) / 30, Math.max(24, slotHeight * 0.75)),
    };
  } catch (error) {
    console.error('Error calculating event position for', startTime, endTime, error);
    return { top: 0, height: 30 };
  }
};

/**
 * Parse a time string and convert to minutes since midnight
 */
export const timeToMinutes = (timeStr: string): number => {
  try {
    const [hours = '0', minutes = '0'] = timeStr.split(':');
    return parseInt(hours, 10) * 60 + parseInt(minutes, 10);
  } catch {
    return 0;
  }
};

/**
 * Calculate current time line position
 */
export const getCurrentTimePosition = (slotHeight = 40): number => {
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes();
  return (minutes * slotHeight) / 30;
};

/**
 * Check if an event should be shown on a specific date
 */
export const isEventOnDate = (event: any, date: Date): boolean => {
  try {
    const eventDate = new Date(event.start);
    return (
      eventDate.getFullYear() === date.getFullYear() &&
      eventDate.getMonth() === date.getMonth() &&
      eventDate.getDate() === date.getDate()
    );
  } catch {
    return false;
  }
};

/**
 * Get status-based CSS class for events
 */
export const getEventStatusClass = (status?: string): string => {
  if (!status) return 'event-confirmed';
  const normalizedStatus = String(status).toLowerCase();
  if (normalizedStatus.includes('cancel')) return 'event-cancelled';
  if (normalizedStatus.includes('pending')) return 'event-pending';
  return 'event-confirmed';
};

/**
 * Format date to YYYY-MM-DD
 */
export const formatDateISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Compare if two dates are the same day
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};
